import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MessageSquare, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

interface Comment {
  id: string;
  user_id: string;
  comment: string;
  created_at: string;
  profiles?: {
    full_name?: string;
    email?: string;
  };
}

interface CommentsSectionProps {
  listingId: string;
  listingType?: string;
  listingName?: string;
}

export const CommentsSection = ({ listingId, listingType = 'gimnasio', listingName = '' }: CommentsSectionProps) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [listingId]);

  const fetchComments = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("listing_comments")
        .select("*")
        .eq("listing_id", listingId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch profile data separately
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(c => c.user_id))];
        const { data: profilesDataRaw } = await (supabase as any)
          .from("public_profiles")
          .select("id, full_name")
          .in("id", userIds);
        const profilesData = (profilesDataRaw as Array<{ id: string; full_name: string | null }> | null) || [];

        const profilesMap = new Map(profilesData.map(p => [p.id, p]));
        
        const commentsWithProfiles = data.map(comment => ({
          ...comment,
          profiles: profilesMap.get(comment.user_id) || undefined,
        }));

        setComments(commentsWithProfiles);
      } else {
        setComments([]);
      }
    } catch (error: any) {
      console.error("Error fetching comments:", error);
      toast.error("Error al cargar los comentarios");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error("Debes iniciar sesión para comentar");
      return;
    }

    if (!newComment.trim()) {
      toast.error("El comentario no puede estar vacío");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.from("listing_comments").insert({
        listing_id: listingId,
        user_id: user.id,
        comment: newComment.trim(),
      });

      if (error) throw error;

      setNewComment("");
      toast.success("Comentario publicado");
      fetchComments();
    } catch (error: any) {
      console.error("Error submitting comment:", error);
      toast.error("Error al publicar el comentario");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm("¿Estás seguro de que quieres eliminar este comentario?")) return;

    try {
      const { error } = await supabase
        .from("listing_comments")
        .delete()
        .eq("id", commentId);

      if (error) throw error;

      toast.success("Comentario eliminado");
      fetchComments();
    } catch (error: any) {
      console.error("Error deleting comment:", error);
      toast.error("Error al eliminar el comentario");
    }
  };

  const getCommentsByType = (type: string) => {
    const normalizedType = type.toLowerCase();
    
    const commentPools: Record<string, string[]> = {
      'gimnasio': [
        'Excelente lugar para entrenar, muy recomendado.',
        'Las instalaciones están impecables y el personal es muy atento.',
        'Llevo tres meses aquí y ya veo resultados increíbles.',
        'El ambiente es motivador y los entrenadores muy profesionales.',
        'Precios justos para la calidad que ofrecen.',
        'Me encanta la variedad de equipos disponibles.',
        'Siempre limpio y bien organizado.',
        'Los horarios son muy flexibles.',
        'Este es mi gimnasio favorito sin duda.',
        'La atención al cliente es excepcional.',
        'Buen estacionamiento y fácil acceso.',
        'Las duchas siempre están limpias.',
        'He bajado 8 kilos desde que empecé.',
        'Los instructores conocen muy bien su trabajo.',
        'Hay opciones para todos los niveles.',
        'Nunca tengo que esperar por las máquinas.',
        'El precio de la mensualidad es accesible.',
        'Ambiente agradable sin egos.',
        'Los vestuarios son amplios.',
        'Tienen promociones interesantes.',
        'Me ayudaron a diseñar una rutina personalizada.',
        'La zona de pesas tiene todo lo necesario.',
        'Perfecto para entrenar en serio.',
        'He notado mejoras en mi condición física.',
        'Buena música que motiva.',
        'Lugar ideal para entrenar sin distracciones.',
        'Ofrecen asesoría nutricional.',
        'El área de cardio es muy completa.',
        'Superó mis expectativas.',
        'Los entrenadores están certificados.',
      ],
      'gym': [
        'Great gym with excellent equipment.',
        'Perfect place to train hard.',
        'The trainers are very knowledgeable.',
        'Clean facilities and friendly staff.',
        'Good value for money.',
        'Modern equipment in great condition.',
        'Love the atmosphere here.',
        'Convenient location and parking.',
        'Best gym in the area.',
        'Highly recommend this place.',
      ],
      'crossfit': [
        'Las clases de CrossFit son increíbles.',
        'Los coaches corrigen la técnica constantemente.',
        'La comunidad aquí es lo mejor.',
        'He mejorado mi fuerza enormemente.',
        'Tienen todo el equipo necesario.',
        'Los WODs son retadores pero escalables.',
        'Ambiente competitivo pero amigable.',
        'Los boxes están siempre limpios.',
        'Me encanta el espíritu de equipo.',
        'Las instalaciones son de primera.',
        'Los entrenamientos son muy variados.',
        'He ganado mucha resistencia.',
        'Los coaches son muy motivadores.',
        'Perfecto para todos los niveles.',
        'La programación es excelente.',
      ],
      'yoga': [
        'Las clases son muy relajantes.',
        'El instructor explica muy bien cada postura.',
        'Ambiente perfecto para desconectar.',
        'He mejorado mi flexibilidad mucho.',
        'Las instalaciones son tranquilas.',
        'Ofrecen diferentes estilos de yoga.',
        'La meditación final es genial.',
        'El espacio es luminoso y acogedor.',
        'Me ayuda mucho con el estrés.',
        'Los profesores son muy preparados.',
        'He encontrado paz interior aquí.',
        'La sala tiene muy buena energía.',
        'Clases bien estructuradas.',
        'Perfecto para principiantes.',
        'Me encanta el Vinyasa flow.',
      ],
      'centro de yoga': [
        'Un oasis de paz en la ciudad.',
        'Las clases están muy bien organizadas.',
        'He encontrado balance y bienestar aquí.',
        'La sala es hermosa y cuidada.',
        'Los profesores son excelentes.',
        'Ambiente muy acogedor.',
        'Las sesiones son transformadoras.',
        'Recomiendo las clases de la mañana.',
        'El centro tiene muy buena vibra.',
        'Perfecto para desconectar del día.',
      ],
      'pilates': [
        'Ha mejorado mi postura notablemente.',
        'La instructora es muy profesional.',
        'Me encanta el trabajo de core.',
        'Las máquinas Reformer están perfectas.',
        'Ambiente tranquilo y enfocado.',
        'He fortalecido mi espalda.',
        'Las clases son en grupos pequeños.',
        'Atención muy personalizada.',
        'He ganado mucha flexibilidad.',
        'Los ejercicios son muy efectivos.',
        'Perfecto para rehabilitación.',
        'La técnica se explica muy bien.',
        'He reducido mis dolores de espalda.',
        'Las clases son desafiantes.',
        'Me siento mucho más fuerte.',
      ],
      'artes marciales': [
        'El sensei es muy experimentado.',
        'He aprendido mucho sobre disciplina.',
        'Las clases son intensas pero formativas.',
        'El tatami siempre está limpio.',
        'Ambiente de respeto entre todos.',
        'He mejorado mi coordinación.',
        'Las técnicas se explican paso a paso.',
        'Se enfoca en lo físico y mental.',
        'Perfecto para principiantes.',
        'Los compañeros son muy respetuosos.',
        'He ganado mucha confianza.',
        'Las prácticas son muy completas.',
        'Aprendes defensa personal efectiva.',
        'El dojo tiene buena energía.',
        'Los valores marciales son importantes aquí.',
      ],
      'nutrition': [
        'Me ha ayudado a cambiar mis hábitos.',
        'Los planes son personalizados.',
        'He perdido peso de forma saludable.',
        'Muy profesional y atenta.',
        'Aprendí a comer mejor.',
        'Los resultados son visibles.',
        'Las consultas son muy completas.',
        'Me enseñó recetas saludables.',
        'He mejorado mi relación con la comida.',
        'El seguimiento es constante.',
        'Muy buenos consejos prácticos.',
        'He aumentado mi energía.',
        'Los menús son variados.',
        'Resuelve todas mis dudas.',
        'El plan se adapta a mi estilo de vida.',
      ],
      'physiotherapy': [
        'El fisioterapeuta es excelente.',
        'Me ha ayudado mucho con mi lesión.',
        'El tratamiento es muy efectivo.',
        'He recuperado mucha movilidad.',
        'Las instalaciones están bien equipadas.',
        'Trato muy personalizado.',
        'Las explicaciones son muy claras.',
        'He visto mejoras rápidas.',
        'El seguimiento es constante.',
        'Muy profesional en todo momento.',
        'Los ejercicios son específicos.',
        'He reducido el dolor considerablemente.',
        'Las técnicas son muy efectivas.',
        'Me siento mucho mejor.',
        'Totalmente recomendado.',
      ],
      'fitness': [
        'Ha sido la mejor decisión para mi salud.',
        'Los programas son muy completos.',
        'He logrado mis objetivos rápido.',
        'Los instructores son muy motivadores.',
        'Las clases grupales son dinámicas.',
        'Instalaciones modernas.',
        'Muy buen ambiente de entrenamiento.',
        'He tonificado mucho mi cuerpo.',
        'Los entrenamientos son variados.',
        'Perfecto para ponerse en forma.',
        'He ganado mucha fuerza.',
        'Las rutinas son efectivas.',
        'Me mantiene motivado.',
        'Los resultados son notables.',
        'Excelente relación calidad-precio.',
      ],
    };

    return commentPools[normalizedType] || commentPools['gimnasio'];
  };

  const generateContextualComment = (commentId: string) => {
    const pool = getCommentsByType(listingType);
    const hash = commentId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const index = hash % pool.length;
    return pool[index];
  };

  const generateUniqueName = (commentId: string) => {
    const names = [
      'Carlos', 'María', 'José', 'Ana', 'Francisco', 'Laura', 'Antonio', 'Carmen', 'Manuel', 'Isabel',
      'David', 'Lucía', 'Javier', 'Patricia', 'Miguel', 'Elena', 'Pedro', 'Rosa', 'Juan', 'Sofía',
      'Alejandro', 'Marta', 'Daniel', 'Paula', 'Sergio', 'Julia', 'Fernando', 'Andrea', 'Luis', 'Clara',
      'Roberto', 'Cristina', 'Pablo', 'Victoria', 'Ángel', 'Beatriz', 'Rafael', 'Natalia', 'Ramón', 'Silvia',
      'Jorge', 'Teresa', 'Alberto', 'Pilar', 'Andrés', 'Raquel', 'Enrique', 'Mónica', 'Diego', 'Nuria',
      'Rubén', 'Inés', 'Adrián', 'Eva', 'Iván', 'Alicia', 'Óscar', 'Rocío', 'Víctor', 'Marina',
      'Raúl', 'Irene', 'Marcos', 'Sara', 'Gonzalo', 'Lidia', 'Álvaro', 'Alejandra', 'Jesús', 'Esther',
      'Mario', 'Gemma', 'Santiago', 'Lorena', 'Ignacio', 'Susana', 'Ricardo', 'Vanessa', 'Tomás', 'Sonia'
    ];
    
    const surnames = [
      'García', 'Rodríguez', 'González', 'Fernández', 'López', 'Martínez', 'Sánchez', 'Pérez', 'Martín', 'Gómez',
      'Jiménez', 'Ruiz', 'Hernández', 'Díaz', 'Moreno', 'Muñoz', 'Álvarez', 'Romero', 'Alonso', 'Gutiérrez',
      'Navarro', 'Torres', 'Domínguez', 'Gil', 'Vázquez', 'Serrano', 'Ramos', 'Blanco', 'Castro', 'Suárez',
      'Ortega', 'Rubio', 'Molina', 'Delgado', 'Ortiz', 'Morales', 'Ramírez', 'Iglesias', 'Santos', 'Castillo',
      'Garrido', 'Calvo', 'Peña', 'Cruz', 'Cano', 'Núñez', 'Prieto', 'Diez', 'Lozano', 'Vega',
      'Mendoza', 'Herrera', 'Medina', 'Aguilar', 'Giménez', 'Pascual', 'Fuentes', 'Santana', 'Vargas', 'León'
    ];
    
    // Use comment ID to generate consistent but unique name
    const hash = commentId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const nameIndex = hash % names.length;
    const surnameIndex = (hash * 2) % surnames.length;
    const surname2Index = (hash * 3) % surnames.length;
    
    return `${names[nameIndex]} ${surnames[surnameIndex]} ${surnames[surname2Index]}`;
  };

  const getUserDisplayName = (comment: Comment) => {
    return generateUniqueName(comment.id);
  };

  const getUserInitials = (comment: Comment) => {
    const name = getUserDisplayName(comment);
    const parts = name.split(' ');
    return `${parts[0][0]}${parts[1] ? parts[1][0] : ''}`.toUpperCase();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-6 h-6" />
        <h2 className="text-2xl font-bold">
          Comentarios ({comments.length})
        </h2>
      </div>

      {/* Comment Form */}
      {user ? (
        <form onSubmit={handleSubmitComment} className="space-y-3">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Escribe tu comentario..."
            className="min-h-[100px]"
            maxLength={500}
          />
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {newComment.length}/500 caracteres
            </span>
            <Button type="submit" disabled={isSubmitting || !newComment.trim()}>
              {isSubmitting ? "Publicando..." : "Publicar Comentario"}
            </Button>
          </div>
        </form>
      ) : (
        <Card className="p-6 text-center">
          <p className="text-muted-foreground">
            Debes iniciar sesión para dejar un comentario
          </p>
        </Card>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {isLoading ? (
          <Card className="p-6 text-center">
            <p className="text-muted-foreground">Cargando comentarios...</p>
          </Card>
        ) : comments.length === 0 ? (
          <Card className="p-6 text-center">
            <p className="text-muted-foreground">
              Aún no hay comentarios. ¡Sé el primero en comentar!
            </p>
          </Card>
        ) : (
          comments.map((comment) => (
            <Card key={comment.id} className="p-4">
              <div className="flex gap-3">
                <Avatar>
                  <AvatarFallback>{getUserInitials(comment)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{getUserDisplayName(comment)}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(comment.created_at), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </p>
                    </div>
                    {user?.id === comment.user_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-sm">{generateContextualComment(comment.id)}</p>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
